import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Typography
  } from "@mui/material";
import {
  Archive,
  Cancel,
  CheckCircle,
  Schedule
  } from "@mui/icons-material";
import { ArchiveUserData } from "../../interfaces/IArchiveData";
import { HistoricalDataStatus } from "../../interfaces/IUsers";

interface HistoricalDataBannerProps {
  archiveData: ArchiveUserData | null;
  status: HistoricalDataStatus;
  canImport: boolean;
  loading: boolean;
  error: string | null;
  onImport: () => void;
  onReject: () => void;
  onSkip: () => void;
}

/**
 * Banner informujący o dostępnych danych historycznych
 */
export default function HistoricalDataBanner({
  archiveData,
  status,
  canImport,
  loading,
  error,
  onImport,
  onReject,
  onSkip,
}: HistoricalDataBannerProps) {
  // Nie pokazuj bannera jeśli nie ma danych historycznych lub użytkownik już podjął decyzję
  if (!archiveData || !canImport) {
    return null;
  }

  const getStatusChip = () => {
    switch (status) {
      case HistoricalDataStatus.IMPORTED:
        return <Chip icon={<CheckCircle />} label="Zaimportowano" color="success" size="small" />;
      case HistoricalDataStatus.REJECTED:
        return <Chip icon={<Cancel />} label="Odrzucono" color="error" size="small" />;
      case HistoricalDataStatus.SKIPPED:
        return <Chip icon={<Schedule />} label="Pominięto" color="warning" size="small" />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Alert
        severity="info"
        icon={<Archive />}
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: "primary.main",
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Dane historyczne dostępne do importu
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Posiadamy dane historyczne Twojego konta z poprzedniej wersji systemu. Znaleziono{" "}
              <strong>{archiveData.participants.length} zawodników</strong> do zaimportowania.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import nie usunie istniejących zawodników - zostaną one dodane do obecnej listy.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {getStatusChip()}
              {loading && <CircularProgress size={20} />}
            </Box>

            <ButtonGroup variant="outlined" size="small" disabled={loading}>
              <Button
                onClick={onImport}
                startIcon={<CheckCircle />}
                color="success"
                disabled={loading}
              >
                TAK, zaimportuj wszystko
              </Button>
              <Button onClick={onSkip} startIcon={<Schedule />} color="warning" disabled={loading}>
                NIE, ale chcę mieć możliwość późniejszego importu
              </Button>
              <Button onClick={onReject} startIcon={<Cancel />} color="error" disabled={loading}>
                NIE, nie będę nigdy importował danych historycznych
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Alert>
    </Box>
  );
}
